/**
 * Y GO Shuttle Services France - Main Application
 * Premium VTC Transport Platform
 * LOT 9 - Added Public Homepage / Vitrine & SEO Pages
 * LOT 10 - CMS & Internationalization
 */
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import { ProtectedRoute, RoleProtectedRoute, PublicOnlyRoute } from './components/auth/ProtectedRoute';
import { Toaster } from 'sonner';

// Pages
import HomePage from './pages/HomePage';
import TransfertsPage from './pages/TransfertsPage';
import CroisieresExcursionsPage from './pages/CroisieresExcursionsPage';
import ExcursionDetailPage from './pages/ExcursionDetailPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AdminDashboard from './pages/AdminDashboard';
import ClientDashboard from './pages/ClientDashboard';
import ChauffeurDashboard from './pages/ChauffeurDashboard';
import BookingPage from './pages/booking/BookingPage';
import PaymentSuccessPage from './pages/booking/PaymentSuccessPage';
import PaymentCancelPage from './pages/booking/PaymentCancelPage';
import PaymentRedirectPage from './pages/booking/PaymentRedirectPage';
import TrackingPage from './pages/booking/TrackingPage';

// Admin Pages (LOT 5) — lazy-loaded (kept out of the main bundle for faster public page loads)
const AdminBookingsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminBookingsPage })));
const AdminBookingDetailPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminBookingDetailPage })));
const AdminClientsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminClientsPage })));
const AdminClientDetailPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminClientDetailPage })));
const AdminChauffeursPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminChauffeursPage })));
const AdminPaymentsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminPaymentsPage })));
const AdminTariffsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminTariffsPage })));
const AdminEmailLogsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminEmailLogsPage })));
const AdminSecurityPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminSecurityPage })));
const AdminDevisPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminDevisPage })));
const AdminExcursionsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminExcursionsPage })));
const AdminExcursionsReservationsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminExcursionsReservationsPage })));
const AdminExcursionGuidesPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminExcursionGuidesPage })));
const AdminExcursionCitiesPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminExcursionCitiesPage })));
const AdminExcursionExperiencesPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminExcursionExperiencesPage })));
const AdminExcursionAgenciesPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminExcursionAgenciesPage })));
const AdminExcursionEmailTemplatesPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminExcursionEmailTemplatesPage })));
const AdminSharedTripsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminSharedTripsPage })));
const AdminConfigurationPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminConfigurationPage })));
const AdminCMSPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminCMSPage })));
const AdminCMSPageEditor = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminCMSPageEditor })));
const AdminHomepageEditor = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminHomepageEditor })));
const AdminReportsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminReportsPage })));
const AdminPlanningPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminPlanningPage })));
const AdminVehiclesPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminVehiclesPage })));
const AdminPartnersPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminPartnersPage })));
const AdminDriversPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminDriversPage })));
const AdminStatisticsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminStatisticsPage })));
const AdminBillingPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminBillingPage })));
const AdminSettingsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminSettingsPage })));
const AdminDispatchMapPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminDispatchMapPage })));
const AdminTrackingLinksPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminTrackingLinksPage })));
const AdminWalletPayoutsPage = lazy(() => import('./pages/admin').then(m => ({ default: m.AdminWalletPayoutsPage })));
const AdminSettingsHub = lazy(() => import('./pages/admin/AdminSettingsHub'));
import ShortPaymentLinkPage from './pages/public/ShortPaymentLinkPage';

// Chauffeur Pages (LOT 6)
import {
  ChauffeurMissionsPage,
  ChauffeurMissionDetailPage,
  ChauffeurProfilPage,
  ChauffeurExcursionsPage,
  ChauffeurChatPage,
  ChauffeurShiftPage
} from './pages/chauffeur';
import ChauffeurWalletPage from './driver-app/pages/wallet/WalletPage';

// Chauffeur Mobile Layout (EPIC 1)
import ChauffeurMobileLayout from './components/chauffeur/ChauffeurMobileLayout';

// Legal & Error Pages (LOT 8)
import LegalPages from './pages/LegalPages';
import ErrorPage, { Error403, Error404, Error500 } from './pages/ErrorPage';

// New Pages
import EntreprisesPage from './pages/EntreprisesPage';
import FlottePage from './pages/FlottePage';
import PrestationsPage from './pages/PrestationsPage';
import MyReservationsPage from './pages/MyReservationsPage';

// Driver Registration Pages (Module Inscription)
import DriverRegisterPage from './pages/driver/DriverRegisterPage';
import DriverVerifyPage from './pages/driver/DriverVerifyPage';

// Partner Registration Page (Module Inscription)
import PartnerRegisterPage from './pages/partner/PartnerRegisterPage';
import PartnerDashboardPage from './pages/partner/PartnerDashboardPage';

// Pro Registration (unified switcher)
import ProRegisterPage from './pages/register/ProRegisterPage';

// Password Reset
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ActivateAccountPage from './pages/main/ActivateAccountPage';
import ConfirmDeletionPage from './pages/main/ConfirmDeletionPage';
import DeleteAccountPage from './pages/main/DeleteAccountPage';

// Public Tracking Page
import PublicTrackingPage from './pages/PublicTrackingPage';

// Public Quote Acceptance Page (Phase 3)
import PublicQuotePage from './pages/PublicQuotePage';

// Subcontractors partner page (password-protected, no nav link)
import SubcontractorsRatesPage from './pages/subcontractors/SubcontractorsRatesPage';

// Admin RH Pages (Phase 5) — lazy-loaded
const AdminRHPage = lazy(() => import('./pages/admin/rh/AdminRHPage'));
const AdminRHEmployeeDetailPage = lazy(() => import('./pages/admin/rh/AdminRHEmployeeDetailPage'));
const AdminRHCalendarPage = lazy(() => import('./pages/admin/rh/AdminRHCalendarPage'));
const AdminRHShiftsPage = lazy(() => import('./pages/admin/rh/AdminRHShiftsPage'));

// Employee Desktop RH Space
import EmployeeDesktopLayout from './pages/employees/EmployeeDesktopLayout';
import EmployeeDashboardPage from './pages/employees/EmployeeDashboardPage';
import EmployeeContractPage from './pages/employees/EmployeeContractPage';
import EmployeeDocumentsDesktopPage from './pages/employees/EmployeeDocumentsDesktopPage';
import EmployeePayslipsDesktopPage from './pages/employees/EmployeePayslipsDesktopPage';
import DesktopSchedulePage from './pages/employees/DesktopSchedulePage';
import DesktopLeavePage from './pages/employees/DesktopLeavePage';
import DesktopHistoryPage from './pages/employees/DesktopHistoryPage';

// Standalone Driver App (PWA) — Replaced by native app download page
import DriverAppDownloadPage from './pages/DriverAppDownloadPage';

// Analytics
import GoogleTagManager from './components/GoogleTagManager';
import CookieConsent from './components/CookieConsent';

// Styles
import './index.css';

// Canonical URL per route (single source of truth — avoids duplicate canonical tags)
const CANONICAL_BASE = 'https://www.y-go.fr';
const RouteMeta = () => {
  const location = useLocation();
  const canonical = `${CANONICAL_BASE}${location.pathname}`;
  return (
    <Helmet>
      <link rel="canonical" href={canonical} />
    </Helmet>
  );
};

// Suspense fallback while lazy route chunks load
const RouteFallback = () => (
  <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-[#C9A24D]/20 border-t-[#C9A24D] rounded-full animate-spin" />
  </div>
);

// Home redirect component - redirects based on role
const HomeRedirect = () => {
  const { isAuthenticated, loading, isSuperAdmin, isChauffeur } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#C9A24D]/20 border-t-[#C9A24D] rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isSuperAdmin()) {
    return <Navigate to="/admin" replace />;
  }

  if (isChauffeur()) {
    return <Navigate to="/app" replace />;
  }

  return <Navigate to="/client" replace />;
};

// Redirect /tracking → /suivi while preserving query params (e.g. ?ref=XXX from legacy emails)
const TrackingRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/suivi${location.search}`} replace />;
};

// App Routes Component
const AppRoutes = () => {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicOnlyRoute>
            <RegisterPage />
          </PublicOnlyRoute>
        } 
      />

      {/* Booking tunnel - accessible to all (auth required at step 5) */}
      <Route path="/booking" element={<BookingPage />} />
      
      {/* Excursion detail page (dynamic) */}
      <Route path="/excursions/:slug" element={<ExcursionDetailPage />} />
      
      {/* Entreprises page */}
      <Route path="/entreprises" element={<EntreprisesPage />} />
      
      {/* Flotte page */}
      <Route path="/flotte" element={<FlottePage />} />
      
      {/* Prestations page */}
      <Route path="/prestations" element={<PrestationsPage />} />
      
      {/* My Reservations page - accessible to all */}
      <Route path="/mes-reservations" element={<MyReservationsPage />} />
      
      {/* Booking tracking page - accessible to all */}
      <Route path="/suivi" element={<TrackingPage />} />
      <Route path="/tracking" element={<TrackingRedirect />} />

      {/* Public Tracking Page - Driver live tracking (no auth) */}
      <Route path="/track/:token" element={<PublicTrackingPage />} />
      <Route path="/devis/:token" element={<PublicQuotePage />} />
      
      {/* Payment result pages (LOT 4) */}
      <Route path="/booking/payment" element={<PaymentRedirectPage />} />
      <Route path="/booking/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/booking/payment/cancel" element={<PaymentCancelPage />} />

      {/* Custom payment link short URLs */}
      <Route path="/p/:slug" element={<ShortPaymentLinkPage />} />

      {/* Driver Registration & Verification (Module Inscription) */}
      <Route path="/driver/register" element={<DriverRegisterPage />} />
      <Route path="/driver/verify" element={<DriverVerifyPage />} />

      {/* Subcontractors partner page — password-protected, no nav link */}
      <Route path="/subcontractors/rates" element={<SubcontractorsRatesPage />} />

      {/* Partner Registration (Module Inscription) */}
      <Route path="/partner/register" element={<PartnerRegisterPage />} />

      {/* Pro Registration - unified switcher (Chauffeur / Partenaire) */}
      <Route path="/register/pro" element={<ProRegisterPage />} />

      {/* Password Reset */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/activate-account" element={<ActivateAccountPage />} />
      <Route path="/confirm-deletion" element={<ConfirmDeletionPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />

      {/* Partner Dashboard — PARTNER role only */}
      <Route
        path="/partner/dashboard"
        element={
          <RoleProtectedRoute allowedRoles={['PARTNER']}>
            <PartnerDashboardPage />
          </RoleProtectedRoute>
        }
      />

      {/* Protected routes - Admin */}
      <Route
        path="/admin"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN', 'DISPATCHER']}>
            <AdminDashboard />
          </RoleProtectedRoute>
        }
      />
      {/* Redirect old bookings routes to planning */}
      <Route
        path="/admin/bookings"
        element={<Navigate to="/admin/planning" replace />}
      />
      <Route
        path="/admin/bookings/:bookingId"
        element={<Navigate to="/admin/planning" replace />}
      />
      <Route
        path="/admin/clients"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN', 'DISPATCHER']} requiredPermission="clients">
            <AdminClientsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:clientId"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN', 'DISPATCHER']} requiredPermission="clients">
            <AdminClientDetailPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/chauffeurs"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN', 'DISPATCHER']} requiredPermission="drivers">
            <AdminChauffeursPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="billing">
            <AdminPaymentsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/tariffs"
        element={<Navigate to="/admin/settings?tab=tariffs" replace />}
      />
      <Route
        path="/admin/emails"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN']} requiredPermission="reports">
            <AdminEmailLogsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/security"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN']} requiredPermission="settings">
            <AdminSecurityPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/devis"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="billing">
            <AdminDevisPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/excursions"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="excursions">
            <AdminExcursionsReservationsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/excursions/guides"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="excursions">
            <AdminExcursionGuidesPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/excursions/cities"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="excursions">
            <AdminExcursionCitiesPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/excursions/experiences"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="excursions">
            <AdminExcursionExperiencesPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/excursions/agencies"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="excursions">
            <AdminExcursionAgenciesPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/excursions/templates"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="excursions">
            <AdminExcursionEmailTemplatesPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/excursions/shared-trips"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="excursions">
            <AdminSharedTripsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="settings">
            <AdminSettingsHub />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/branding"
        element={<Navigate to="/admin/settings?tab=branding" replace />}
      />
      <Route
        path="/admin/configuration"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="settings">
            <AdminConfigurationPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/cms"
        element={<Navigate to="/admin/settings?tab=cms" replace />}
      />
      <Route
        path="/admin/cms/homepage"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN']} requiredPermission="cms">
            <AdminHomepageEditor />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/cms/pages/:pageSlug"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN']} requiredPermission="cms">
            <AdminCMSPageEditor />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={<Navigate to="/admin/settings?tab=reports" replace />}
      />
      
      {/* Planning - Admin & Dispatcher */}
      <Route
        path="/admin/planning"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN', 'DISPATCHER']} requiredPermission="planning">
            <AdminPlanningPage />
          </RoleProtectedRoute>
        }
      />
      
      {/* New Admin Pages */}
      <Route
        path="/admin/vehicles"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN', 'DISPATCHER']} requiredPermission="vehicles">
            <AdminVehiclesPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="partners">
            <AdminPartnersPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/drivers"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN', 'DISPATCHER']} requiredPermission="drivers">
            <AdminDriversPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/statistics"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="statistics">
            <AdminStatisticsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/billing"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="billing">
            <AdminBillingPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/wallet"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']} requiredPermission="wallet">
            <AdminWalletPayoutsPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/dispatch"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN', 'DISPATCHER']} requiredPermission="dispatch">
            <AdminDispatchMapPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/tracking-links"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN', 'DISPATCHER']} requiredPermission="tracking">
            <AdminTrackingLinksPage />
          </RoleProtectedRoute>
        }
      />

      {/* Admin RH Module (Phase 5) */}
      <Route
        path="/admin/rh"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
            <AdminRHPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/rh/employees/:id"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
            <AdminRHEmployeeDetailPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/rh/calendar"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
            <AdminRHCalendarPage />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/admin/rh/shifts"
        element={
          <RoleProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
            <AdminRHShiftsPage />
          </RoleProtectedRoute>
        }
      />

      {/* Protected routes - Client (CLIENT role only) */}
      <Route
        path="/client"
        element={
          <RoleProtectedRoute allowedRoles={['CLIENT']}>
            <ClientDashboard />
          </RoleProtectedRoute>
        }
      />

      {/* Chauffeur PWA Web App — DISABLED. All access redirected to the native app download page. */}
      <Route path="/chauffeur" element={<Navigate to="/app" replace />} />
      <Route path="/chauffeur/*" element={<Navigate to="/app" replace />} />

      {/* Legal Pages */}
      <Route path="/legal/:type" element={<LegalPages />} />
      
      {/* Error Pages (LOT 8) */}
      <Route path="/error/403" element={<Error403 />} />
      <Route path="/error/404" element={<Error404 />} />
      <Route path="/error/500" element={<Error500 />} />

      {/* Home - Public Homepage (LOT 9) */}
      <Route path="/" element={<HomePage />} />

      {/* SEO Pages (LOT 9) */}
      <Route path="/transferts" element={<TransfertsPage />} />
      <Route path="/croisieres-excursions" element={<CroisieresExcursionsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* Legacy redirects */}
      <Route path="/excursions" element={<Navigate to="/croisieres-excursions" replace />} />
      <Route path="/croisieres" element={<Navigate to="/croisieres-excursions" replace />} />
      <Route path="/reserver" element={<Navigate to="/booking" replace />} />

      {/* Dashboard redirect for authenticated users */}
      <Route path="/dashboard" element={<HomeRedirect />} />

      {/* Driver App Download Page (replaces PWA) */}
      <Route path="/app" element={<DriverAppDownloadPage />} />
      <Route path="/app/*" element={<DriverAppDownloadPage />} />

      {/* Employee Desktop RH Space */}
      <Route path="/employees" element={<EmployeeDesktopLayout />}>
        <Route index element={<EmployeeDashboardPage />} />
        <Route path="contract" element={<EmployeeContractPage />} />
        <Route path="documents" element={<EmployeeDocumentsDesktopPage />} />
        <Route path="payslips" element={<EmployeePayslipsDesktopPage />} />
        <Route path="schedule" element={<DesktopSchedulePage />} />
        <Route path="leave" element={<DesktopLeavePage />} />
        <Route path="history" element={<DesktopHistoryPage />} />
      </Route>

      {/* Catch all - 404 */}
      <Route path="*" element={<Error404 />} />
    </Routes>
    </Suspense>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <GoogleTagManager />
          <RouteMeta />
          {/* Temporarily disabled for testing excursion missions */}
          {/* <CookieConsent /> */}
          <div className="min-h-screen bg-[#0B0B0B]">
            <AppRoutes />
            <Toaster position="top-right" richColors theme="dark" />
          </div>
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
